import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitCompareArrows, FileText, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ScrollPanel } from "./ui/scroll-panel";

/**
 * Generic "Compare with…" launcher used on the Analysis page.
 * Asks the backend for compatible counterparty documents + relationship keys,
 * lets the user pick one, kicks off POST /api/comparisons, and navigates to
 * the resulting /comparisons/:id page.
 */
export default function CompareLauncher({ doc, onLaunch }) {
  const { t } = useTranslation("components");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(null); // candidate id mid-run

  useEffect(() => {
    if (!open || !doc?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/documents/${doc.id}/compare-candidates`);
        if (!cancelled) setCandidates(res.data?.candidates || []);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load candidates");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, doc?.id]);

  const handleRun = async (candidate, relationshipKey) => {
    setRunning(candidate.id);
    try {
      const res = await api.post("/comparisons", {
        type: relationshipKey,
        doc_a_id: doc.id,
        doc_b_id: candidate.id,
      });
      onLaunch?.(res.data);
      toast.success("Comparison ready");
      setOpen(false);
      navigate(`/comparisons/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Comparison failed");
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-testid="compare-launcher-button"
        variant="outline"
        className="gap-1.5 h-9"
      >
        <GitCompareArrows size={14} strokeWidth={1.75} />
        {t("compare.cta")}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
          data-testid="compare-launcher-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-6 pb-4 shrink-0">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Cross-document analysis</p>
                <h2 className="font-display text-xl font-semibold text-zinc-950">Compare this document with…</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="compare-launcher-close"
                className="w-7 h-7 grid place-items-center rounded hover:bg-zinc-100"
              >
                <X size={14} />
              </button>
            </div>
            {loading ? (
              <div className="py-10 px-6 grid place-items-center shrink-0" data-testid="compare-launcher-loading">
                <Loader2 size={18} className="animate-spin text-zinc-400" />
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-zinc-500 py-6 px-6 text-center shrink-0" data-testid="compare-launcher-empty">
                No compatible documents in this workspace yet.
                Upload a counterparty document first.
              </p>
            ) : (
              <ScrollPanel className="px-6 pb-6">
                <ul className="space-y-2" data-testid="compare-launcher-candidates">
                  {candidates.map((cand) => (
                    <li
                      key={cand.id}
                      data-testid={`compare-launcher-candidate-${cand.id}`}
                      className="px-3 py-2.5 rounded-lg border border-zinc-200 hover:border-zinc-300 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={13} className="text-zinc-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-zinc-900 truncate">{cand.filename}</p>
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500">{cand.document_type}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {cand.relationship_keys.map((rk) => (
                          <Button
                            key={rk}
                            size="sm"
                            onClick={() => handleRun(cand, rk)}
                            disabled={running === cand.id}
                            data-testid={`compare-launcher-run-${cand.id}-${rk}`}
                            className="h-7 text-xs bg-zinc-950 hover:bg-zinc-800 text-white"
                          >
                            {running === cand.id ? "Running…" : `Run ${rk.replace(/_/g, " ")}`}
                          </Button>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </ScrollPanel>
            )}
          </div>
        </div>
      )}
    </>
  );
}
